import { Button, Grid, Typography } from '@mui/material';
import {
  FC,
  Fragment,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { PageErrorBoundary } from '../components';

type ModalContextValue = {
  showModal: (id: string, modal: ReactNode) => void;
  hideModal: (id: string) => void;
  hideAll: () => void;
  isModalOpen: (id: string) => boolean;
};

const defaultValue = {
  showModal: () => undefined,
  hideModal: () => undefined,
  hideAll: () => undefined,
  isModalOpen: () => false,
};

export const ReloadPageError = () => {
  const { t } = useTranslation();
  const reloadPage = () => window.location.reload();

  return (
    <Grid container flexDirection="column" justifyContent="center" alignItems="center" padding="40px" gap="40px">
      <Grid item>
        <Typography variant="h1">{t('errorBoundary.title')}</Typography>
      </Grid>
      <Grid item display="flex" flexDirection="column" alignItems="center" gap="40px">
        <Typography variant="p16">{t('errorBoundary.message')}</Typography>
        <Button variant="contained" color="secondary" onClick={reloadPage}>
          {t('button.backHome')}
        </Button>
      </Grid>
    </Grid>
  );
};

export const ModalContext = createContext<ModalContextValue>(defaultValue);

export const idKeepModal = 'keep-modal';

export const ModalContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [modal, setModal] = useState<Record<string, ReactNode>>({});

  const showModal = useCallback((id: string, modal: ReactNode) => setModal(prev => ({ ...prev, [id]: modal })), []);
  const hideModal = useCallback((id: string) => setModal(({ [id]: _, ...other }) => ({ ...other })), []);
  const hideAll = useCallback(
    () =>
      setModal(prev => ({
        ...(prev[idKeepModal] ? { [idKeepModal]: prev[idKeepModal] } : {}),
      })),
    [],
  );
  const isModalOpen = useCallback((id: string) => !!modal[id], [modal]);

  const contextValue = useMemo<ModalContextValue>(
    () => ({
      showModal,
      hideModal,
      hideAll,
      isModalOpen,
    }),
    [showModal, hideModal, hideAll, isModalOpen],
  );
  return (
    <ModalContext.Provider value={contextValue}>
      <PageErrorBoundary failback={<ReloadPageError />}>
        {Object.entries(modal).map(([id, modal]) => (
          <Fragment key={id}>{cloneElement(modal as ReactElement, { id })}</Fragment>
        ))}
        {children}
      </PageErrorBoundary>
    </ModalContext.Provider>
  );
};

export const useModalContext = () => useContext(ModalContext);
